import { useEffect, useRef, useState } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError, HAITI_DEPARTMENTS, productImageUrl } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { User, MapPin, Lock, Package, Camera, Plus, Trash2, Edit, Star, ChevronRight, Gift, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../context/I18nContext";

const AddressForm = ({ initial, onSave, onCancel }) => {
  const { t } = useI18n();
  const [f, setF] = useState(initial || { label: "Maison", full_name: "", phone: "", address: "", city: "", department: "Ouest", notes: "", is_default: false });
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => { e.preventDefault(); try { await onSave(f); toast.success(t("save")); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <form onSubmit={submit} className="bg-gray-50 border rounded-xl p-4 space-y-2" data-testid="address-form">
      <div className="grid md:grid-cols-2 gap-2">
        <input required placeholder="Étiquette (Maison, Bureau...)" value={f.label} onChange={upd("label")} className="border rounded px-3 py-2 text-sm" data-testid="addr-label" />
        <input required placeholder={t("fullName")} value={f.full_name} onChange={upd("full_name")} className="border rounded px-3 py-2 text-sm" data-testid="addr-name" />
        <input required placeholder={t("phone")} value={f.phone} onChange={upd("phone")} className="border rounded px-3 py-2 text-sm" data-testid="addr-phone" />
        <input required placeholder={t("city")} value={f.city} onChange={upd("city")} className="border rounded px-3 py-2 text-sm" data-testid="addr-city" />
        <input required placeholder={t("address")} value={f.address} onChange={upd("address")} className="md:col-span-2 border rounded px-3 py-2 text-sm" data-testid="addr-street" />
        <select value={f.department} onChange={upd("department")} className="border rounded px-3 py-2 text-sm bg-white" data-testid="addr-dept">
          {HAITI_DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_default} onChange={upd("is_default")} /> {t("defaultAddress")}</label>
      </div>
      <div className="flex gap-2 pt-1">
        <button type="submit" className="je-btn-primary" data-testid="addr-save">{t("save")}</button>
        <button type="button" onClick={onCancel} className="border px-4 py-2 rounded">{t("cancel")}</button>
      </div>
    </form>
  );
};

export default function AccountPage() {
  const { user, loading, refresh } = useAuth();
  const { t } = useI18n();
  const { format: formatGdes } = useCurrency();
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab") || "dashboard";
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const avatarRef = useRef(null);

  const loadRewards = () => api.get("/rewards/me").then(({ data }) => setRewards(data)).catch(() => {});

  useEffect(() => {
    if (user) {
      setName(user.name || ""); setPhone(user.phone || "");
      api.get("/orders/me").then(({ data }) => setOrders(data)).catch(() => {});
      api.get("/addresses").then(({ data }) => setAddresses(data)).catch(() => {});
      loadRewards();
    }
  }, [user]);

  if (loading) return <div className="je-container py-20 text-center text-gray-500">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const saveProfile = async () => {
    try { await api.patch("/auth/me", { name, phone }); await refresh(); toast.success(t("save")); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/password-change", { current_password: currentPw, new_password: newPw }); setCurrentPw(""); setNewPw(""); toast.success(t("save")); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/auth/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh(); toast.success(t("save"));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const saveAddress = async (data) => {
    if (editing) await api.put(`/addresses/${editing.address_id}`, data);
    else await api.post("/addresses", data);
    const { data: list } = await api.get("/addresses"); setAddresses(list);
    setEditing(null); setAdding(false);
  };

  const deleteAddr = async (id) => {
    if (!window.confirm(t("delete") + " ?")) return;
    await api.delete(`/addresses/${id}`);
    setAddresses(addresses.filter(a => a.address_id !== id));
    toast.success(t("delete"));
  };

  const avatarSrc = user.picture ? (user.picture.startsWith("http") ? user.picture : `${process.env.REACT_APP_BACKEND_URL}${user.picture}`) : null;

  return (
    <div className="je-container py-10" data-testid="account-page">
      <div className="flex items-center gap-4">
        <div className="w-20 h-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center text-3xl font-bold text-gray-400" style={{fontFamily:"Outfit"}}>
          {avatarSrc ? <img src={avatarSrc} alt="" className="w-full h-full object-cover" /> : (user.name?.[0] || "?").toUpperCase()}
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold" style={{fontFamily:"Outfit"}}>{user.name}</h1>
          <p className="text-gray-500">{user.email}</p>
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="mt-8">
        <TabsList className="je-scroll-tabs flex flex-nowrap overflow-x-auto whitespace-nowrap h-auto max-w-full gap-1 p-1 justify-start" data-testid="account-tabslist">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">📊 Dashboard</TabsTrigger>
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1" /> {t("profile")}</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses"><MapPin className="w-4 h-4 mr-1" /> {t("addresses")}</TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password"><Lock className="w-4 h-4 mr-1" /> {t("password")}</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="w-4 h-4 mr-1" /> {t("orders")}</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="tab-rewards"><Gift className="w-4 h-4 mr-1" /> Récompenses{rewards?.available > 0 && <span className="ml-1 bg-[#E3000F] text-white text-[10px] px-1.5 py-0.5 rounded-full">{rewards.available}</span>}</TabsTrigger>
          <TabsTrigger value="bonus" data-testid="tab-bonus">💰 Mes bonus</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">🔔 Notifications</TabsTrigger>
          <TabsTrigger value="ambassador" data-testid="tab-ambassador">🎯 Ambassadeur</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6">
          <DashboardTab />
        </TabsContent>
        <TabsContent value="bonus" className="mt-6">
          <BonusTab />
        </TabsContent>
        <TabsContent value="notifications" className="mt-6">
          <NotificationsTab />
        </TabsContent>

        <TabsContent value="profile" className="mt-6">
          <div className="bg-white border rounded-xl p-6 max-w-xl">
            <h3 className="font-bold mb-4">{t("editProfile")}</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => avatarRef.current?.click()} className="inline-flex items-center gap-2 border rounded-md px-3 py-2 text-sm hover:bg-gray-50" data-testid="upload-avatar">
                  <Camera className="w-4 h-4" /> {t("uploadPhoto")}
                </button>
                <input ref={avatarRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{t("fullName")}</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="w-full border rounded-md px-3 py-2 mt-1" data-testid="profile-name" />
              </div>
              <div>
                <label className="text-xs text-gray-500 uppercase tracking-wider font-semibold">{t("phone")}</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+509..." className="w-full border rounded-md px-3 py-2 mt-1" data-testid="profile-phone" />
              </div>
              <button onClick={saveProfile} className="je-btn-primary" data-testid="profile-save">{t("save")}</button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="addresses" className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold">{t("addresses")}</h3>
            {!adding && !editing && <button onClick={() => setAdding(true)} className="je-btn-primary inline-flex items-center gap-1" data-testid="add-address-btn"><Plus className="w-4 h-4" /> {t("addAddress")}</button>}
          </div>
          {(adding || editing) && <div className="mb-4"><AddressForm initial={editing} onSave={saveAddress} onCancel={() => { setAdding(false); setEditing(null); }} /></div>}
          <div className="grid md:grid-cols-2 gap-4">
            {addresses.map((a) => (
              <div key={a.address_id} className="bg-white border rounded-xl p-4" data-testid={`address-${a.address_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-bold flex items-center gap-2">{a.label} {a.is_default && <Star className="w-4 h-4 je-star fill-current" />}</p>
                    <p className="text-sm text-gray-700 mt-1">{a.full_name}</p>
                    <p className="text-sm text-gray-700">{a.address}</p>
                    <p className="text-sm text-gray-700">{a.city}, {a.department}</p>
                    <p className="text-sm text-gray-500 mt-1">{a.phone}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => setEditing(a)} className="text-[#0052CC] p-1"><Edit className="w-4 h-4" /></button>
                    <button onClick={() => deleteAddr(a.address_id)} className="text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              </div>
            ))}
            {addresses.length === 0 && <p className="col-span-full text-center text-sm text-gray-500 py-8">Aucune adresse enregistrée.</p>}
          </div>
        </TabsContent>

        <TabsContent value="password" className="mt-6">
          <form onSubmit={savePassword} className="bg-white border rounded-xl p-6 max-w-xl space-y-3" data-testid="password-form">
            <h3 className="font-bold">{t("changePassword")}</h3>
            <input type="password" required placeholder={t("currentPassword")} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} className="w-full border rounded-md px-3 py-2" data-testid="pw-current" />
            <input type="password" required minLength={6} placeholder={t("newPassword")} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="w-full border rounded-md px-3 py-2" data-testid="pw-new" />
            <button type="submit" className="je-btn-primary" data-testid="pw-save">{t("save")}</button>
          </form>
        </TabsContent>

        <TabsContent value="orders" className="mt-6">
          {orders.length === 0 ? (
            <div className="bg-gray-50 rounded-xl p-10 text-center"><Package className="w-10 h-10 text-gray-400 mx-auto" /><p className="text-gray-500 mt-2">Aucune commande.</p><Link to="/products" className="je-btn-blue inline-block mt-4">{t("shop")}</Link></div>
          ) : (
            <div className="space-y-3">
              {orders.map((o) => (
                <Link key={o.order_id} to={`/track/${o.tracking_number}`} className="flex items-center gap-4 bg-white border rounded-xl p-4 hover:border-[#0052CC] transition">
                  <div className="w-12 h-12 bg-[#F3F4F6] rounded-full flex items-center justify-center"><Package className="w-5 h-5 text-[#0052CC]" /></div>
                  <div className="flex-1 min-w-0"><p className="font-bold">{o.tracking_number}</p><p className="text-sm text-gray-500">{new Date(o.created_at).toLocaleDateString()} • {o.items.length} article(s)</p></div>
                  <span className={`je-status-pill je-status-${o.status}`}>{o.status}</span>
                  <span className="font-bold" style={{fontFamily:"Outfit"}}>{formatGdes(o.total)}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rewards" className="mt-6">
          <RewardsTab rewards={rewards} addresses={addresses} onClaimed={() => { loadRewards(); api.get("/orders/me").then(({ data }) => setOrders(data)); }} />
        </TabsContent>

        <TabsContent value="ambassador" className="mt-6">
          <AmbassadorTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RewardsTab({ rewards, addresses, onClaimed }) {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const { format: formatGdes } = useCurrency();
  const defaultAddr = addresses.find((a) => a.is_default) || addresses[0] || null;

  if (!rewards) return <p className="text-gray-500">Chargement…</p>;

  const { delivered_orders_count, threshold, next_reward_in, available, claimed, eligible_products, claim_history } = rewards;
  const progressPct = Math.min(100, ((delivered_orders_count % threshold) / threshold) * 100);

  const claim = async () => {
    if (!selectedProduct) return;
    if (!defaultAddr) {
      toast.error("Ajoutez une adresse de livraison avant de réclamer votre cadeau");
      return;
    }
    setClaiming(true);
    try {
      await api.post("/rewards/claim", {
        product_id: selectedProduct.product_id,
        shipping_address: {
          full_name: defaultAddr.full_name,
          phone: defaultAddr.phone,
          address: defaultAddr.address,
          city: defaultAddr.city,
          department: defaultAddr.department,
          notes: defaultAddr.notes || "",
        },
      });
      toast.success("🎁 Cadeau réclamé ! Il sera livré prochainement.");
      setSelectedProduct(null);
      onClaimed();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClaiming(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="rewards-tab">
      {/* Progress card */}
      <div className="bg-gradient-to-br from-[#0052CC] to-[#003D99] text-white rounded-2xl p-6 relative overflow-hidden">
        <Sparkles className="w-24 h-24 absolute -top-4 -right-4 opacity-10" />
        <div className="flex items-center gap-2 mb-1">
          <Gift className="w-5 h-5" />
          <p className="text-xs uppercase tracking-widest font-semibold opacity-90">Programme fidélité</p>
        </div>
        <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: "Outfit" }}>
          {available > 0
            ? `🎁 Vous avez ${available} cadeau${available > 1 ? "x" : ""} disponible${available > 1 ? "s" : ""} !`
            : `Encore ${next_reward_in} commande${next_reward_in > 1 ? "s" : ""} pour votre prochain cadeau`}
        </h3>
        <p className="text-sm opacity-80 mb-4">
          {delivered_orders_count} commande{delivered_orders_count > 1 ? "s" : ""} livrée{delivered_orders_count > 1 ? "s" : ""} · toutes les {threshold} commandes = 1 cadeau
        </p>
        <div className="h-2 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white transition-all" style={{ width: `${available > 0 ? 100 : progressPct}%` }} />
        </div>
        <div className="flex justify-between text-xs mt-2 opacity-80">
          <span>{delivered_orders_count % threshold} / {threshold}</span>
          <span>{claimed} déjà réclamé{claimed > 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Eligible products */}
      {available > 0 && (
        <div>
          <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Choisissez votre cadeau</h3>
          {eligible_products.length === 0 ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              Aucun cadeau disponible pour le moment. Nous ajoutons régulièrement de nouveaux produits — revenez bientôt !
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {eligible_products.map((p) => (
                <button
                  key={p.product_id}
                  onClick={() => setSelectedProduct(p)}
                  className={`text-left bg-white border-2 rounded-xl p-4 transition ${selectedProduct?.product_id === p.product_id ? "border-[#E3000F] shadow-lg" : "border-gray-200 hover:border-gray-300"}`}
                  data-testid={`reward-product-${p.product_id}`}
                >
                  <div className="w-full h-32 bg-gray-50 rounded flex items-center justify-center overflow-hidden mb-3">
                    <img src={productImageUrl(p.images?.[0])} alt="" className="max-h-full max-w-full object-contain" />
                  </div>
                  <p className="font-bold text-sm line-clamp-2">{p.name}</p>
                  <p className="text-xs text-gray-500 mt-1">Valeur : {formatGdes(p.price)}</p>
                  <span className="inline-block mt-2 bg-[#E3000F] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded">Gratuit</span>
                </button>
              ))}
            </div>
          )}

          {selectedProduct && (
            <div className="mt-4 bg-white border-2 border-[#E3000F] rounded-xl p-5">
              <p className="font-bold mb-1">🎁 Vous avez choisi : <span className="text-[#E3000F]">{selectedProduct.name}</span></p>
              {defaultAddr ? (
                <p className="text-sm text-gray-600 mb-3">
                  Livraison à : <b>{defaultAddr.full_name}</b>, {defaultAddr.address}, {defaultAddr.city}, {defaultAddr.department}
                </p>
              ) : (
                <p className="text-sm text-red-600 mb-3">Ajoutez une adresse par défaut dans l&apos;onglet Adresses avant de réclamer.</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={claim}
                  disabled={claiming || !defaultAddr}
                  className="je-btn-primary disabled:opacity-50"
                  data-testid="claim-reward-btn"
                >
                  {claiming ? "Traitement…" : "Réclamer mon cadeau"}
                </button>
                <button onClick={() => setSelectedProduct(null)} className="border px-4 py-2 rounded">Annuler</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Claim history */}
      {claim_history.length > 0 && (
        <div>
          <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Mes cadeaux réclamés</h3>
          <div className="space-y-2">
            {claim_history.map((c) => (
              <Link key={c.order_id} to={`/track/${c.tracking_number}`} className="flex items-center gap-3 bg-white border rounded-xl p-3 hover:border-[#E3000F] transition">
                <Gift className="w-5 h-5 text-[#E3000F]" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold line-clamp-1">{c.items?.[0]?.name}</p>
                  <p className="text-xs text-gray-500">{new Date(c.created_at).toLocaleDateString()} · {c.tracking_number}</p>
                </div>
                <span className={`je-status-pill je-status-${c.status}`}>{c.status}</span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function AmbassadorTab() {
  const [amb, setAmb] = useState(undefined);
  const [application, setApplication] = useState(undefined);
  const [copied, setCopied] = useState(false);
  const { format: formatGdes } = useCurrency();

  useEffect(() => {
    api.get("/ambassadors/me").then(({ data }) => setAmb(data)).catch(() => setAmb(null));
    api.get("/ambassador-applications/me").then(({ data }) => setApplication(data)).catch(() => setApplication(null));
  }, []);

  if (amb === undefined || application === undefined) return <p className="text-gray-500">Chargement…</p>;

  // Approved ambassador — show the full panel
  if (amb) {
    const link = `${window.location.origin}/?ref=${amb.code}`;
    const copyLink = async () => {
      try { await navigator.clipboard.writeText(link); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success("Lien copié"); }
      catch { toast.error("Impossible de copier"); }
    };
    return (
      <div className="space-y-6" data-testid="ambassador-panel">
        <div className="bg-gradient-to-br from-[#0052CC] to-[#001A5C] text-white rounded-2xl p-6 shadow-lg">
          <p className="text-xs uppercase tracking-widest opacity-80">Votre code ambassadeur</p>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-3xl md:text-4xl font-extrabold font-mono" data-testid="ambassador-code">{amb.code}</span>
            <span className="ml-auto bg-white text-[#0052CC] text-xs font-bold px-3 py-1 rounded-full">Actif</span>
          </div>
          <p className="text-sm mt-3 opacity-90">Réduction accordée à vos filleuls : <b>{amb.discount_percent}%</b> • Votre commission : <b>{amb.commission_percent || 0}%</b></p>
        </div>
        <div className="bg-white border rounded-xl p-5">
          <label className="text-xs uppercase font-semibold text-gray-500">Votre lien de parrainage</label>
          <div className="flex gap-2 mt-2">
            <input readOnly value={link} className="flex-1 border rounded px-3 py-2 text-sm font-mono bg-gray-50" data-testid="ambassador-link" />
            <button onClick={copyLink} className="je-btn-primary inline-flex items-center gap-1" data-testid="copy-ambassador-link">
              {copied ? "✓ Copié" : "Copier"}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">Partagez ce lien : chaque personne qui commande à partir de votre lien vous rapporte une commission.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="ambassador-stats">
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Clics</p><p className="text-2xl font-extrabold mt-1" data-testid="stat-clicks">{amb.visits_count || 0}</p></div>
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Ventes</p><p className="text-lg font-extrabold mt-1" data-testid="stat-sales">{formatGdes(amb.sales_total || 0)}</p></div>
          <div className="bg-white border rounded-xl p-4"><p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">Commissions</p><p className="text-lg font-extrabold mt-1 text-[#16A34A]" data-testid="stat-commissions">{formatGdes(amb.commission_total || 0)}</p></div>
        </div>
      </div>
    );
  }

  // Pending or rejected application
  if (application) {
    if (application.status === "pending") {
      return (
        <div className="bg-white border rounded-xl p-8 text-center max-w-xl" data-testid="ambassador-pending">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
            ⏳ En cours
          </div>
          <p className="text-lg font-bold mt-3" style={{ fontFamily: "Outfit" }}>Traitement du dossier en cours</p>
          <p className="text-sm text-gray-600 mt-2">Votre demande a bien été reçue le <b>{new Date(application.created_at).toLocaleDateString()}</b>. Notre équipe étudie actuellement votre candidature. Vous recevrez un email dès qu'une décision aura été prise.</p>
          <div className="mt-6 text-left bg-gray-50 rounded-lg p-4 text-sm">
            <p><b>Nom :</b> {application.name}</p>
            <p><b>Email :</b> {application.email}</p>
            <p><b>Téléphone :</b> {application.phone}</p>
            <p><b>Profil :</b> {application.profile_type}</p>
          </div>
        </div>
      );
    }
    if (application.status === "rejected") {
      return (
        <div className="bg-white border rounded-xl p-8 max-w-xl" data-testid="ambassador-rejected">
          <div className="inline-flex items-center gap-2 bg-red-100 text-red-800 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full">
            Demande refusée
          </div>
          <p className="text-lg font-bold mt-3" style={{ fontFamily: "Outfit" }}>Votre candidature n'a pas été retenue</p>
          {application.review_notes && (
            <p className="text-sm text-gray-600 mt-3"><b>Note :</b> {application.review_notes}</p>
          )}
          <p className="text-sm text-gray-500 mt-4">Vous pouvez retenter votre chance ultérieurement en soumettant une nouvelle demande.</p>
          <Link to="/become-ambassador" className="je-btn-primary inline-block mt-4">Nouvelle demande</Link>
        </div>
      );
    }
  }

  // No application yet
  return (
    <div className="bg-white border rounded-xl p-8 text-center max-w-xl" data-testid="ambassador-not-enrolled">
      <p className="text-lg font-bold" style={{ fontFamily: "Outfit" }}>Devenez ambassadeur John Elektwo 🎯</p>
      <p className="text-sm text-gray-600 mt-2">Rejoignez notre programme et gagnez une commission sur chaque commande générée par votre lien unique.</p>
      <Link to="/become-ambassador" className="je-btn-primary inline-block mt-4" data-testid="apply-ambassador-btn">Faire ma demande</Link>
    </div>
  );
}


function DashboardTab() {
  const [data, setData] = useState(null);
  const { format: formatGdes } = useCurrency();
  useEffect(() => { api.get("/me/dashboard").then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <p className="text-gray-500">Chargement…</p>;
  const Card = ({ label, value, color, icon }) => (
    <div className={`backdrop-blur-sm bg-white/70 border border-white/60 rounded-2xl p-5 shadow-sm relative overflow-hidden`}>
      <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10 ${color}`} />
      <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold">{label}</p>
      <p className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>{value}</p>
      <span className="absolute right-4 top-4 text-2xl">{icon}</span>
    </div>
  );
  return (
    <div className="space-y-5" data-testid="customer-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <Card label="Total dépensé" value={formatGdes(data.total_spent)} color="bg-[#0052CC]" icon="💳" />
        <Card label="Commandes réussies" value={data.orders_completed} color="bg-[#16A34A]" icon="✅" />
        <Card label="En cours" value={data.orders_in_progress} color="bg-[#F59E0B]" icon="⏳" />
        <Card label="Annulées" value={data.orders_cancelled} color="bg-[#E3000F]" icon="✕" />
      </div>
      <div className="backdrop-blur-sm bg-white/70 border border-white/60 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold" style={{ fontFamily: "Outfit" }}>Dernières commandes</h3>
          <span className="text-xs text-gray-500">{data.orders_total} au total</span>
        </div>
        {data.recent_orders.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Aucune commande pour le moment.</p>
        ) : (
          <div className="space-y-2">
            {data.recent_orders.map((o) => (
              <Link key={o.order_id} to={`/track/${o.tracking_number}`} className="flex items-center justify-between p-3 rounded-lg bg-white border hover:border-[#0052CC] transition" data-testid={`dash-order-${o.order_id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm">{o.tracking_number}</p>
                  <p className="text-xs text-gray-500">{new Date(o.created_at).toLocaleDateString()} • {(o.items || []).length} article(s)</p>
                </div>
                <div className="text-right shrink-0">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${o.status === "delivered" ? "bg-green-100 text-green-700" : o.status === "cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{o.status}</span>
                  <p className="font-bold mt-1 text-sm">{formatGdes(o.total)}</p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <div className="backdrop-blur-sm bg-gradient-to-br from-[#0052CC]/95 to-[#001A5C]/95 text-white border border-white/20 rounded-2xl p-5 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-widest opacity-80">Solde bonus</p>
          <p className="text-2xl md:text-3xl font-extrabold mt-1" style={{ fontFamily: "Outfit" }}>{formatGdes(data.bonus_balance)}</p>
        </div>
        <span className="text-4xl">🎁</span>
      </div>
    </div>
  );
}

function NotificationsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/notifications").then(({ data }) => setItems(data.notifications || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/notifications/${id}/read`).catch(() => {}); load(); };
  const markAll = async () => { await api.post("/notifications/read-all").catch(() => {}); load(); };
  return (
    <div data-testid="notifications-tab">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>Notifications</h3>
        {items.some(n => !n.read) && <button onClick={markAll} className="text-xs text-[#0052CC] font-semibold" data-testid="mark-all-read">Tout marquer lu</button>}
      </div>
      {items.length === 0 ? (
        <div className="bg-white border rounded-xl p-8 text-center text-gray-500">Aucune notification pour le moment.</div>
      ) : (
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.notification_id} className={`border rounded-xl p-4 flex items-start gap-3 ${n.read ? "bg-white" : "bg-blue-50/40 border-[#0052CC]/30"}`} data-testid={`notif-${n.notification_id}`}>
              <div className="text-2xl">{n.icon === "gift" ? "🎁" : n.icon === "package" ? "📦" : n.icon === "mail" ? "✉️" : "🔔"}</div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{n.title}</p>
                {n.body && <p className="text-xs text-gray-600 mt-1">{n.body}</p>}
                <p className="text-[10px] text-gray-400 mt-1">{new Date(n.created_at).toLocaleString()}</p>
              </div>
              {!n.read && <button onClick={() => markRead(n.notification_id)} className="text-xs text-[#0052CC] hover:underline" data-testid={`mark-read-${n.notification_id}`}>Marquer lu</button>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BonusTab() {
  const [wallet, setWallet] = useState(null);
  const { format: formatGdes } = useCurrency();
  useEffect(() => { api.get("/me/wallet").then(({ data }) => setWallet(data)).catch(() => {}); }, []);
  if (!wallet) return <p className="text-gray-500">Chargement…</p>;
  return (
    <div className="space-y-5" data-testid="bonus-tab">
      <div className="bg-gradient-to-br from-[#0052CC] to-[#001A5C] text-white rounded-2xl p-6 shadow-lg">
        <p className="text-xs uppercase tracking-widest opacity-80">Solde bonus disponible</p>
        <p className="text-4xl md:text-5xl font-extrabold mt-2" style={{ fontFamily: "Outfit" }} data-testid="bonus-balance">{formatGdes(wallet.balance)}</p>
        <p className="text-xs opacity-80 mt-2">Utilisable sur vos prochaines commandes.</p>
      </div>
      <div className="bg-white border rounded-2xl p-5">
        <h3 className="font-bold mb-3" style={{ fontFamily: "Outfit" }}>Historique</h3>
        {wallet.transactions.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Aucun mouvement pour le moment. Tentez votre chance à la Roue !</p>
        ) : (
          <ul className="divide-y">
            {wallet.transactions.map((t) => (
              <li key={t.tx_id} className="py-3 flex items-center justify-between" data-testid={`bonus-tx-${t.tx_id}`}>
                <div>
                  <p className="font-semibold text-sm">{t.kind === "wheel_win_credit" ? "🎡 Gain Roue de la chance" : t.kind === "order_debit" ? "🛒 Utilisé sur commande" : t.kind}</p>
                  <p className="text-xs text-gray-500">{new Date(t.created_at).toLocaleString()}</p>
                </div>
                <span className={`font-extrabold ${t.amount > 0 ? "text-[#16A34A]" : "text-[#E3000F]"}`}>{t.amount > 0 ? "+" : ""}{formatGdes(t.amount)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

